import { Box, CircularProgress, Stack } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import Search from "./components/Search";
import {
  CampaignSearchRequest,
  useGetCampaignSummary,
  useSearchCampaign,
} from "../../api/campaign";
import CampaignDataGrid from "../../components/campaign/campaignDataGrid/CampaignDataGrid";
import DataDisplayCard from "../../components/molecules/DataDisplayCard";
import { iconMapping } from "../../helpers/Campaign";
import { useCampaignStore, useSetCampaignStore } from "../../stores/campaignStore";
import { usePermissionStore } from "../../stores/permissionStore";

const Campaign = () => {
  const [isSearch, setIsSearch] = useState(false);
  const campaignStore = useCampaignStore();
  const setCampaignStore = useSetCampaignStore();

  const [request, setRequest] = useState<CampaignSearchRequest>({
    pagination: {
      currentPage: 1,
      pageSize: 10,
    },
    permission_Token: usePermissionStore.getState().permissionToken ?? "",
  });

  const { data: dataCampaign, isFetching } = useSearchCampaign(request, isSearch);

  useEffect(() => {
    if (dataCampaign) {
      setCampaignStore(dataCampaign.data);
    }
  }, [dataCampaign, setCampaignStore]);

  const summaryRequestBody = useMemo(() => {
    const s: any = (request as any)?.status;
    const statusArr = Array.isArray(s) ? s : s ? [s] : [];
    return {
      status: statusArr,
      permission_Token: request.permission_Token ?? "",
    };
  }, [request]);

  const { data: summaryData, isLoading: isSummaryLoading } = useGetCampaignSummary(
    summaryRequestBody,
    true
  );

  const getIconKeyByLabel = (label: string) => {
    switch (label) {
      case "Campaigns":
        return "campaigns";
      case "Sent":
        return "sent";
      case "Engaged":
        return "engaged";
      case "Rate":
        return "rate";
      default:
        return undefined;
    }
  };

  const overviewData = [
    {
      label: "Campaigns",
      value:
        summaryData?.data?.totalCount?.toString() ??
        (dataCampaign as any)?.data?.pagination?.total?.toString() ??
        "0",
      icon: iconMapping(getIconKeyByLabel("Campaigns") as string) as any,
    },
    {
      label: "Sent",
      value: summaryData?.data?.sentCount?.toString() ?? "0",
      icon: iconMapping(getIconKeyByLabel("Sent") as string) as any,
    },
    {
      label: "Engaged",
      value: summaryData?.data?.engagedCount?.toString() ?? "0",
      icon: iconMapping(getIconKeyByLabel("Engaged") as string) as any,
    },
    {
      label: "Rate",
      value:
        typeof summaryData?.data?.rate === "number"
          ? `${(summaryData.data.rate * 100).toFixed(1)}%`
          : "0.0%",
      icon: iconMapping(getIconKeyByLabel("Rate") as string) as any,
    },
  ];

  return (
    <Stack paddingX={4}>
      <Search request={request} setRequest={setRequest} setIsSearch={setIsSearch} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(4, 1fr)",
            lg: "repeat(7, 1fr)",
            xl: "repeat(7, 1fr)",
          },
          gap: 2,
          width: "100%",
          mb: 3,
          position: "relative",
        }}
      >
        {overviewData.map((item, index) => (
          <DataDisplayCard
            key={index}
            icon={item.icon}
            title={item.label}
            value={isSummaryLoading ? "…" : item.value}
          />
        ))}
        {isSummaryLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <CircularProgress size={40} thickness={4.5} sx={{ color: "#FF7A00" }} />
          </Box>
        )}
      </Box>
      <Box sx={{ position: "relative" }}>
        <CampaignDataGrid
          onPageChange={(page) => {
            setRequest({
              ...request,
              pagination: { ...request.pagination, currentPage: page },
            });
          }}
          data={campaignStore}
          loading={isFetching}
        />
        {isFetching && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <CircularProgress size={40} thickness={4.5} sx={{ color: "#FF7A00" }} />
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default Campaign;
